
import { format, isToday, differenceInMinutes } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { forwardRef, useEffect, useRef } from "react";
import { ListLoadingState } from "@/components/loading/ListLoadingState";

interface MessageThreadProps {
  messages: any[];
  currentUserId: string | null;
  isLoading?: boolean;
}

export const MessageThread = forwardRef<HTMLDivElement, MessageThreadProps>(
  ({ messages, currentUserId, isLoading }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, [messages]);

    const shouldShowTimestamp = (currentMsg: any, prevMsg: any) => {
      if (!prevMsg) return true;
      return differenceInMinutes(
        new Date(currentMsg.created_at),
        new Date(prevMsg.created_at)
      ) > 5;
    };

    const formatMessageDate = (date: Date) => {
      if (isToday(date)) {
        return format(date, "'Today at' h:mm a");
      }
      return format(date, "MMM d 'at' h:mm a");
    };

    if (isLoading) {
      return <ListLoadingState />;
    }

    if (!messages?.length) {
      return (
        <div className="h-full flex items-center justify-center text-muted-foreground">
          Start the conversation
        </div>
      );
    }

    return (
      <div 
        className="flex-1 p-4 overflow-y-auto h-[calc(100vh-9rem)]" 
        ref={scrollRef}
      >
        <div>
          {messages.map((message, index) => (
            <div key={message.id} className="space-y-2">
              {shouldShowTimestamp(message, messages[index - 1]) && (
                <div className="flex justify-center my-4">
                  <span className="text-xs text-muted-foreground">
                    {formatMessageDate(new Date(message.created_at))}
                  </span>
                </div>
              )}
              <div
                className={`flex gap-2 mb-1 ${
                  message.sender_id === currentUserId ? "justify-end" : "justify-start"
                }`}
              >
                {message.sender_id !== currentUserId && (
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={message.sender?.image_url} />
                    <AvatarFallback>
                      {message.sender?.first_name?.[0]}
                      {message.sender?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    message.sender_id === currentUserId
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm break-words">{message.content}</p>
                </div>
                {message.sender_id === currentUserId && (
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={message.sender?.image_url} />
                    <AvatarFallback>
                      {message.sender?.first_name?.[0]}
                      {message.sender?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            </div>
          ))}
          <div ref={ref} id="thread-end" />
        </div>
      </div>
    );
  }
);

MessageThread.displayName = "MessageThread";
